/**
 * The game engine: a small state machine the UI drives.
 *
 * The UI never mutates `PlayerState` directly. It calls these functions, each
 * of which produces a new session and a report describing what changed, which
 * is what the interface animates.
 */

import type { BoardNode, LogCategory, PlayerState, StatDelta, TurnReport } from './types';
import { nextNodes } from './board';
import { advanceYears } from './systems';
import { drawEvent, presentEvent, resolveChoice } from './events';
import type { PresentedEvent, ResolutionResult } from './events';
import { buildEndingReport } from './ending';
import { ACTIONS_BY_ID, actionAffordable, actionEffects } from './actions';
import type { PlayerAction } from './actions';
import { applyEffects, pushLog } from './effects';
import { Rng } from './rng';
import { evaluateAll } from './conditions';
import { STAT_KEYS } from './types';

export type Phase = 'board' | 'event' | 'outcome' | 'ended';

export interface GameSession {
  state: PlayerState;
  phase: Phase;
  currentEvent: PresentedEvent | null;
  lastResult: (ResolutionResult & { eventTitle: string }) | null;
  lastTurn: TurnReport | null;
  /** Action already used at the current node. */
  actionUsed: boolean;
}

export function createSession(state: PlayerState): GameSession {
  const session: GameSession = {
    state,
    phase: state.alive ? 'board' : 'ended',
    currentEvent: null,
    lastResult: null,
    lastTurn: null,
    actionUsed: false,
  };
  if (session.phase !== 'board') return session;
  // A queued opening beat (or a follow-up saved mid-chain) must be presented
  // straight away. Waiting for the first board move meant a new game opened on
  // an empty board with no context at all.
  if (state.pendingEvents.length > 0) return loadEvent(session);
  return session;
}

function snapshotStats(state: PlayerState): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k of STAT_KEYS) out[k] = state.stats[k];
  out['cash'] = state.finance.cash;
  return out;
}

const STAT_LABELS: Record<string, string> = {
  health: 'Health',
  energy: 'Energy',
  stress: 'Stress',
  happiness: 'Happiness',
  purpose: 'Purpose',
  cash: 'Cash',
};

function statDeltas(before: Record<string, number>, after: Record<string, number>): StatDelta[] {
  const out: StatDelta[] = [];
  for (const key of Object.keys(before)) {
    const b = Math.round(before[key] as number);
    const a = Math.round(after[key] as number);
    if (a !== b) out.push({ key, label: STAT_LABELS[key] ?? key, before: b, after: a });
  }
  return out;
}

export function currentNode(session: GameSession): BoardNode | undefined {
  return session.state.board.nodes[session.state.nodeId];
}

export function availableMoves(session: GameSession): BoardNode[] {
  if (session.phase !== 'board') return [];
  return nextNodes(session.state.board, session.state.nodeId);
}

function finish(session: GameSession): GameSession {
  const state = session.state;
  if (!state.ending) {
    state.alive = false;
    if (!state.causeOfDeath) state.causeOfDeath = 'old age';
    state.ending = buildEndingReport(state);
    pushLog(state, {
      category: 'life',
      tone: 'milestone',
      title: `The life of ${state.identity.name}`,
      detail: state.ending.archetypeLabel,
      weight: 11,
      tags: ['ending'],
    });
  }
  return { ...session, phase: 'ended', currentEvent: null };
}

/** Load the next event for the current node, or return to the board. */
function loadEvent(session: GameSession): GameSession {
  const state = session.state;
  const node = state.board.nodes[state.nodeId];
  if (!node) return { ...session, phase: 'board' };

  if (node.kind === 'end') return finish(session);

  const event = drawEvent(state, node.pools);
  if (!event) return { ...session, phase: 'board', currentEvent: null };

  const presented = presentEvent(state, event);
  if (presented.choices.length === 0) {
    // Nothing playable after all — fall through to the board.
    return { ...session, phase: 'board', currentEvent: null };
  }

  // Automatic events resolve without asking, but still report their outcome.
  if (event.automatic && presented.choices.length === 1) {
    const only = presented.choices[0];
    if (only) {
      const result = resolveChoice(state, event, only.choice.id);
      return {
        ...session,
        phase: 'outcome',
        currentEvent: presented,
        lastResult: { ...result, eventTitle: event.title },
      };
    }
  }

  return { ...session, phase: 'event', currentEvent: presented, lastResult: null };
}

/**
 * Step onto an adjacent board node. Time passes, the year is simulated, and an
 * event is drawn for the destination.
 */
export function moveTo(session: GameSession, nodeId: string): GameSession {
  if (session.phase !== 'board') return session;
  const state = session.state;
  const moves = nextNodes(state.board, state.nodeId);
  const target = moves.find((n) => n.id === nodeId);
  if (!target) return session;

  const before = snapshotStats(state);
  const logStart = state.log.length;

  const result = advanceYears(state, target.yearCost);
  state.nodeId = target.id;
  state.visitedNodes.push(target.id);

  const turn: TurnReport = {
    yearsAdvanced: result.years,
    entries: state.log.slice(logStart),
    deltas: statDeltas(before, snapshotStats(state)),
    ended: !state.alive,
  };

  const next: GameSession = { ...session, lastTurn: turn, actionUsed: false, lastResult: null };
  if (!state.alive) return finish(next);
  if (target.milestone) {
    pushLog(state, {
      category: 'life',
      tone: 'milestone',
      title: target.title,
      detail: target.subtitle,
      weight: 6,
      tags: ['milestone', target.stage],
    });
  }
  return loadEvent(next);
}

/** Take one of the offered choices on the current event. */
export function choose(session: GameSession, choiceId: string): GameSession {
  if (session.phase !== 'event' || !session.currentEvent) return session;
  const presented = session.currentEvent;
  const option = presented.choices.find((c) => c.choice.id === choiceId);
  if (!option || !option.enabled) return session;

  const result = resolveChoice(session.state, presented.event, choiceId);
  const next: GameSession = {
    ...session,
    phase: 'outcome',
    lastResult: { ...result, eventTitle: presented.event.title },
  };
  if (!session.state.alive) return finish(next);
  return next;
}

/** Acknowledge an outcome. Chains continue; otherwise control returns to the board. */
export function acknowledge(session: GameSession): GameSession {
  if (session.phase !== 'outcome') return session;
  const state = session.state;
  if (!state.alive) return finish(session);
  if (state.pendingEvents.length > 0) {
    return loadEvent({ ...session, lastResult: null });
  }
  return { ...session, phase: 'board', currentEvent: null };
}

/* ------------------------------------------------------------------ *
 * Actions
 * ------------------------------------------------------------------ */

export interface OfferedAction {
  action: PlayerAction;
  enabled: boolean;
}

/** Actions offered at the current node, grouped by the node's action slots. */
export function availableActions(session: GameSession): OfferedAction[] {
  const node = currentNode(session);
  if (!node || session.phase === 'ended') return [];
  const groups = new Set(node.actions);
  if (groups.size === 0) return [];
  const out: OfferedAction[] = [];
  for (const action of Object.values(ACTIONS_BY_ID)) {
    if (!groups.has(action.group)) continue;
    const enabled = evaluateAll(session.state, action.requires) && actionAffordable(session.state, action);
    if (!enabled) continue;
    out.push({ action, enabled });
  }
  return out;
}

export function takeAction(session: GameSession, actionId: string): GameSession {
  if (session.actionUsed || session.phase === 'ended') return session;
  const action = ACTIONS_BY_ID[actionId];
  if (!action) return session;
  const state = session.state;
  if (!evaluateAll(state, action.requires)) return session;

  const before = snapshotStats(state);
  const logStart = state.log.length;
  const rng = Rng.fromState(state.rng);
  applyEffects({ state, rng, source: action.label }, actionEffects(state, action));
  state.rng = rng.toState();

  pushLog(state, {
    category: actionLogCategory(action.group),
    tone: 'neutral',
    title: action.label,
    weight: 3,
    tags: ['action', ...(action.tags ?? [])],
  });

  const turn: TurnReport = {
    yearsAdvanced: 0,
    entries: state.log.slice(logStart),
    deltas: statDeltas(before, snapshotStats(state)),
    ended: false,
  };
  return { ...session, actionUsed: true, lastTurn: turn };
}

function actionLogCategory(group: PlayerAction['group']): LogCategory {
  switch (group) {
    case 'invest':
      return 'investment';
    case 'retire':
      return 'life';
    case 'leisure':
      return 'wellbeing';
    case 'relationships':
      return 'relationship';
    default:
      return group;
  }
}

/** Force the run to end — used by the "conclude this life" affordance. */
export function endLife(session: GameSession, cause = 'old age'): GameSession {
  session.state.causeOfDeath = cause;
  return finish(session);
}
